/**
 * Spanish Deep-Clean Landing Page
 * Route: /es/limpieza-profunda/:citySlug
 */
import { useParams, Navigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { Guarantee } from "@/components/Guarantee";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";
import { LocationContext } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";
import {
  CheckCircle2, Star, Zap, Shield, Sparkles, BadgeCheck,
  ChevronRight, ArrowRight, Clock, Home, Leaf, Layers
} from "lucide-react";

const BOOKING_URL = "/book";

// ── City data (shared slug-to-name map) ─────────────────────────────────────
const CITY_DATA: Record<string, { name: string; regionSlug: string; testimonialRegion: string; nearbyCities: string[] }> = {
  sacramento:          { name: "Sacramento",        regionSlug: "sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Roseville", "Folsom", "Elk Grove"] },
  roseville:           { name: "Roseville",         regionSlug: "sacramento-area", testimonialRegion: "roseville",     nearbyCities: ["Sacramento", "Rocklin", "Granite Bay"] },
  folsom:              { name: "Folsom",            regionSlug: "sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "El Dorado Hills"] },
  "elk-grove":         { name: "Elk Grove",         regionSlug: "sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Rancho Cordova"] },
  "rancho-cordova":    { name: "Rancho Cordova",    regionSlug: "sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Folsom"] },
  "granite-bay":       { name: "Granite Bay",       regionSlug: "sacramento-area", testimonialRegion: "roseville",     nearbyCities: ["Roseville", "Rocklin"] },
  "el-dorado-hills":   { name: "El Dorado Hills",   regionSlug: "sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Folsom", "Sacramento"] },
  "west-sacramento":   { name: "West Sacramento",   regionSlug: "sacramento-area", testimonialRegion: "sacramento",    nearbyCities: ["Sacramento", "Davis"] },
  "los-angeles":       { name: "Los Ángeles",       regionSlug: "los-angeles",     testimonialRegion: "la",            nearbyCities: ["Santa Mónica", "Pasadena", "Manhattan Beach"] },
  pasadena:            { name: "Pasadena",           regionSlug: "los-angeles",     testimonialRegion: "pasadena",     nearbyCities: ["Arcadia", "San Marino", "Monrovia"] },
  "santa-monica":      { name: "Santa Mónica",       regionSlug: "los-angeles",     testimonialRegion: "west-la",      nearbyCities: ["Venice", "Culver City", "Brentwood"] },
  "manhattan-beach":   { name: "Manhattan Beach",   regionSlug: "los-angeles",     testimonialRegion: "south-la",     nearbyCities: ["Redondo Beach", "Torrance", "El Segundo"] },
  "san-francisco":     { name: "San Francisco",      regionSlug: "san-francisco",   testimonialRegion: "san-francisco", nearbyCities: ["Daly City", "San Mateo", "Burlingame"] },
  "san-jose":          { name: "San José",           regionSlug: "santa-clara",     testimonialRegion: "san-jose",      nearbyCities: ["Santa Clara", "Sunnyvale", "Milpitas"] },
  "palo-alto":         { name: "Palo Alto",          regionSlug: "santa-clara",     testimonialRegion: "silicon-valley", nearbyCities: ["Mountain View", "Menlo Park", "Los Altos"] },
  "walnut-creek":      { name: "Walnut Creek",       regionSlug: "contra-costa",    testimonialRegion: "contra-costa",  nearbyCities: ["Concord", "Pleasant Hill", "Danville"] },
  "san-ramon":         { name: "San Ramon",          regionSlug: "contra-costa",    testimonialRegion: "tri-valley",    nearbyCities: ["Danville", "Dublin", "Pleasanton"] },
  oakland:             { name: "Oakland",            regionSlug: "alameda",          testimonialRegion: "oakland",       nearbyCities: ["Berkeley", "Alameda", "Emeryville"] },
  fremont:             { name: "Fremont",            regionSlug: "alameda",          testimonialRegion: "east-bay",      nearbyCities: ["Union City", "Newark", "Hayward"] },
  pleasanton:          { name: "Pleasanton",         regionSlug: "alameda",          testimonialRegion: "tri-valley",    nearbyCities: ["Dublin", "Livermore", "San Ramon"] },
  dublin:              { name: "Dublin",             regionSlug: "alameda",          testimonialRegion: "tri-valley",    nearbyCities: ["Pleasanton", "Livermore", "San Ramon"] },
  livermore:           { name: "Livermore",          regionSlug: "alameda",          testimonialRegion: "tri-valley",    nearbyCities: ["Pleasanton", "Dublin", "Tracy"] },
  vallejo:             { name: "Vallejo",            regionSlug: "solano",           testimonialRegion: "solano",        nearbyCities: ["Benicia", "Fairfield"] },
  fairfield:           { name: "Fairfield",          regionSlug: "solano",           testimonialRegion: "solano",        nearbyCities: ["Vacaville", "Vallejo"] },
  "san-diego":         { name: "San Diego",          regionSlug: "san-diego",        testimonialRegion: "san-diego",     nearbyCities: ["Chula Vista", "Carlsbad", "Encinitas"] },
  irvine:              { name: "Irvine",             regionSlug: "orange-county",    testimonialRegion: "irvine",        nearbyCities: ["Costa Mesa", "Newport Beach", "Anaheim"] },
  anaheim:             { name: "Anaheim",            regionSlug: "orange-county",    testimonialRegion: "irvine",        nearbyCities: ["Fullerton", "Orange", "Garden Grove"] },
  "huntington-beach":  { name: "Huntington Beach",  regionSlug: "orange-county",    testimonialRegion: "irvine",        nearbyCities: ["Costa Mesa", "Newport Beach"] },
  "long-beach":        { name: "Long Beach",         regionSlug: "los-angeles",      testimonialRegion: "long-beach",    nearbyCities: ["Torrance", "Lakewood", "Signal Hill"] },
};

const DEEP_CLEAN_CHECKLIST = [
  { area: "Cocina", items: ["Interior del horno y quemadores", "Interior y parte de atrás del refrigerador", "Interior del microondas y campana", "Desengrase profundo de gabinetes y gavetas", "Limpieza detrás de electrodomésticos"] },
  { area: "Baños", items: ["Eliminación de sarro y lechada", "Desinfección profunda de inodoro y bidet", "Limpieza de ducha y bañera (hasta el fondo)", "Espejos y accesorios pulidos", "Interior de gabinetes del baño"] },
  { area: "Áreas Generales", items: ["Zócalos, molduras y marcos de puertas", "Ventanas interiores y persianas", "Ventiladores de techo (aspas y carcasa)", "Debajo de muebles y camas", "Limpieza de interruptores, enchufes y manijas"] },
  { area: "Pisos y Alfombras", items: ["Trapeado profundo con desinfectante", "Aspirado bajo muebles y esquinas", "Tratamiento de manchas en alfombras", "Limpieza de bordes y rincones"] },
];

const WHEN_TO_BOOK = [
  { icon: Home, title: "¿Casa nueva?", desc: "Elimina gérmenes y residuos de los dueños anteriores antes de instalarte." },
  { icon: Layers, title: "¿Primavera?", desc: "La limpieza de primavera profunda resetea tu hogar después del invierno." },
  { icon: Star, title: "¿Visitas importantes?", desc: "Impresiona a familia, amigos o inquilinos con un hogar impecable." },
  { icon: Leaf, title: "¿Alérgenos?", desc: "Elimina polvo, ácaros y alérgenos acumulados que la limpieza regular no alcanza." },
];

const SpanishDeepCleanPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const slug = (citySlug || "sacramento").toLowerCase().replace(/\s+/g, "-");
  const city = CITY_DATA[slug];

  if (!city) return <Navigate to="/es/sacramento" replace />;

  const { name, regionSlug, testimonialRegion, nearbyCities } = city;

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: checkRef, isVisible: checkVisible } = useScrollAnimation();
  const { ref: whenRef, isVisible: whenVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <LocationContext.Provider value={{ slug, name, type: "city", regionSlug: regionSlug as any }}>
      <SEO
        title={`Limpieza Profunda en ${name} — Servicio Profesional | Uplevel Cleaning`}
        description={`Servicio de limpieza profunda en ${name}. Lista de más de 80 puntos, productos ecológicos, equipo verificado y Garantía de Satisfacción del 200%. Reserva en 60 segundos.`}
        keywords={`limpieza profunda ${name}, deep cleaning ${name}, limpieza a fondo ${name}, servicio limpieza profunda ${name.toLowerCase()}, uplevel cleaning`}
        url={`https://uplevelcleaning.com/es/limpieza-profunda/${slug}`}
        alternateUrl={`https://uplevelcleaning.com/locations/${slug}`}
        alternateLang="en-US"
        locationName={name}
        regionSlug={regionSlug}
        lang="es"
        includeLocalBusiness
        includeFAQ
        includeService
        serviceName="Limpieza Profunda"
        serviceDescription={`Limpieza profunda profesional en ${name}. Lista de 80+ puntos, precio plano y Garantía del 200%.`}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name, url: `/es/${slug}` },
          { name: "Limpieza Profunda", url: `/es/limpieza-profunda/${slug}` },
        ]}
        reviews={[
          { author: "Cliente Local", rating: 5, reviewBody: `La limpieza profunda de Uplevel en ${name} dejó mi casa como nueva. Encontraron suciedad en lugares que yo ni sabía que existían.`, datePublished: "2026-04-10" },
          { author: "Residente Satisfecha", rating: 5, reviewBody: `Contraté la limpieza profunda en ${name} para la primavera. El equipo fue increíblemente detallista. ¡Vale cada centavo!`, datePublished: "2026-03-28" },
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">

          {/* ── HERO ─────────────────────────────────────────────────────── */}
          <section className="relative py-20 lg:py-32 overflow-hidden bg-background">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-background to-background pointer-events-none" />
            {/* Decorative blob */}
            <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-primary/5 blur-3xl pointer-events-none" />
            <div className="container relative z-10">
              <div ref={heroRef} className={cn("max-w-4xl mx-auto text-center anim-fade-up", heroVisible && "is-visible")}>
                {/* Breadcrumb */}
                <nav className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-6">
                  <Link to="/" className="hover:text-primary transition-colors">Inicio</Link>
                  <ChevronRight className="h-3 w-3" />
                  <Link to={`/es/${slug}`} className="hover:text-primary transition-colors">{name}</Link>
                  <ChevronRight className="h-3 w-3" />
                  <span className="text-foreground font-medium">Limpieza Profunda</span>
                </nav>

                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Sparkles className="h-4 w-4" />
                  Limpieza Profunda Profesional en {name}
                </div>

                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]">
                  Limpieza Profunda en{" "}
                  <span className="text-primary">{name}</span>
                  <br className="hidden md:block" />
                  De la Superficie al Fondo
                </h1>

                <p className="text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
                  La limpieza estándar mantiene tu hogar. La <strong>limpieza profunda</strong> lo restaura. Nuestro equipo verificado va más allá de la superficie para eliminar grasa, sarro, polvo acumulado y gérmenes escondidos.
                </p>
                <p className="text-base text-muted-foreground mb-4 max-w-2xl mx-auto border-l-4 border-primary pl-4 text-left">
                  {getLocalContent(name, regionSlug, 'es').localIssuesText}
                </p>
                <p className="text-lg font-semibold text-primary mb-10">
                  Precio exacto en segundos. Garantía del 200%. Productos ecológicos.
                </p>

                {/* Dual CTA */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                  <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                    <a href={BOOKING_URL}><Zap className="h-5 w-5 mr-2" />Reservar Ahora</a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/10">
                    <a href={BOOKING_URL}><Sparkles className="h-5 w-5 mr-2" />Obtener Precio al Instante</a>
                  </Button>
                </div>

                {/* Trust row */}
                <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /><span>Asegurados y verificados</span></div>
                  <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-primary" /><span>Productos ecológicos</span></div>
                  <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-primary" /><span>Garantía del 200%</span></div>
                </div>
              </div>
            </div>
          </section>

          {/* ── INLINE CTA STRIP ─────────────────────────────────────────── */}
          <section className="py-6 bg-primary text-primary-foreground">
            <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold">¿Tu hogar en {name} necesita un reset completo?</p>
                <p className="text-sm opacity-80">Limpieza profunda a precio plano — sin tarifas por hora, sin sorpresas.</p>
              </div>
              <div className="flex gap-3 shrink-0">
                <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 hover:text-primary font-bold">
                  <a href={BOOKING_URL}><Zap className="h-4 w-4 mr-2" />Reservar</a>
                </Button>
                <Button asChild size="lg" className="bg-white/10 border border-white/30 text-white hover:bg-white/20 hover:text-white font-bold">
                  <a href={BOOKING_URL}><Sparkles className="h-4 w-4 mr-2" />Ver Precio</a>
                </Button>
              </div>
            </div>
          </section>

          {/* ── DIFFERENCE SECTION ───────────────────────────────────────── */}
          <section className="py-20 bg-muted/30">
            <div className="container max-w-5xl">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Limpieza Estándar vs. Limpieza Profunda
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto">
                La limpieza profunda no es solo "más de lo mismo" — es un servicio completamente diferente diseñado para eliminar lo que la limpieza regular no puede alcanzar.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Standard */}
                <div className="rounded-2xl border bg-card p-8">
                  <h3 className="text-xl font-bold mb-6 text-muted-foreground">Limpieza Estándar</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    {["Superficies visibles del baño", "Cocina por encima", "Pisos barridos y trapeados", "Polvo en superficies accesibles", "Papeleras vaciadas"].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <div className="h-2 w-2 rounded-full bg-muted-foreground/40 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Deep clean */}
                <div className="rounded-2xl border-2 border-primary bg-primary/5 p-8 relative overflow-hidden">
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">Recomendado</div>
                  <h3 className="text-xl font-bold mb-6 text-primary">Limpieza Profunda ✓</h3>
                  <ul className="space-y-3">
                    {["Interior de horno, refri y microondas", "Desengrase completo de cocina", "Eliminación de sarro y lechada", "Detrás y debajo de muebles", "Zócalos, marcos y molduras", "Ventanas interiores y persianas", "Ventiladores de techo (aspas)", "80+ puntos cubiertos"].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm font-medium">
                        <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90">
                  <a href={BOOKING_URL}><Zap className="h-5 w-5 mr-2" />Obtener Mi Precio Ahora</a>
                </Button>
              </div>
            </div>
          </section>

          {/* ── CHECKLIST BY AREA ────────────────────────────────────────── */}
          <section className="py-20 bg-background">
            <div className="container">
              <div ref={checkRef} className={cn("text-center mb-14 anim-fade-up", checkVisible && "is-visible")}>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Lista de 80+ Puntos — {name}</h2>
                <p className="text-muted-foreground max-w-xl mx-auto">Cubrimos cada rincón de tu hogar, área por área, para un resultado verdaderamente transformador.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {DEEP_CLEAN_CHECKLIST.map((area, i) => (
                  <div
                    key={i}
                    className={cn("rounded-2xl border bg-card p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 anim-fade-up", checkVisible && "is-visible")}
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <h3 className="font-bold text-lg mb-4 text-primary">{area.area}</h3>
                    <ul className="space-y-2">
                      {area.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── WHEN TO BOOK ─────────────────────────────────────────────── */}
          <section className="py-20 bg-muted/30">
            <div className="container">
              <div ref={whenRef} className={cn("text-center mb-12 anim-fade-up", whenVisible && "is-visible")}>
                <h2 className="text-3xl font-bold mb-3">¿Cuándo Necesitas una Limpieza Profunda?</h2>
                <p className="text-muted-foreground">Algunas situaciones requieren ir más allá de la limpieza regular.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {WHEN_TO_BOOK.map((item, i) => (
                  <div
                    key={i}
                    className={cn("text-center p-6 rounded-2xl border bg-card hover:shadow-md hover:-translate-y-1 transition-all duration-300 anim-fade-up", whenVisible && "is-visible")}
                    style={{ transitionDelay: `${i * 80}ms` }}
                  >
                    <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <item.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── PRICING ──────────────────────────────────────────────────── */}
          <section className="py-20 bg-background">
            <div className="container max-w-4xl">
              <h2 className="text-3xl font-bold text-center mb-4">Precios de Limpieza Profunda en {name}</h2>
              <p className="text-muted-foreground text-center mb-10">Precio plano, sin tarifas por hora. Obtén tu precio exacto en línea en segundos.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { size: "1 Recámara", price: "desde $279", note: "Apartamentos y estudios" },
                  { size: "2–3 Recámaras", price: "desde $359", note: "Casas y condominios medianos", highlight: true },
                  { size: "4+ Recámaras", price: "desde $499", note: "Casas grandes y mansiones" },
                ].map((tier, i) => (
                  <div key={i} className={cn(
                    "rounded-2xl p-6 text-center border transition-all hover:-translate-y-1 hover:shadow-lg",
                    tier.highlight ? "bg-primary text-primary-foreground border-primary shadow-lg" : "bg-card border-border"
                  )}>
                    <p className={cn("text-sm font-semibold mb-1 uppercase tracking-wider", tier.highlight ? "text-primary-foreground/80" : "text-muted-foreground")}>{tier.size}</p>
                    <p className="text-4xl font-extrabold mb-1">{tier.price}</p>
                    <p className={cn("text-sm", tier.highlight ? "text-primary-foreground/80" : "text-muted-foreground")}>{tier.note}</p>
                    {tier.highlight && <span className="mt-3 inline-block bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">Más popular</span>}
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 btn-pulse">
                  <a href={BOOKING_URL}><Zap className="h-5 w-5 mr-2" />Reservar Limpieza Profunda</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-2 border-primary text-primary hover:bg-primary/10">
                  <a href={BOOKING_URL}><Sparkles className="h-5 w-5 mr-2" />Calcular Mi Precio</a>
                </Button>
              </div>
            </div>
          </section>

          {/* ── TESTIMONIALS ─────────────────────────────────────────────── */}
          <SpanishTestimonials region={testimonialRegion as any} />

          {/* ── GUARANTEE ────────────────────────────────────────────────── */}
          <Guarantee />

          {/* ── FAQ ──────────────────────────────────────────────────────── */}
          <section className="py-20 bg-muted/30">
            <div className="container max-w-3xl">
              <h2 className="text-3xl font-bold text-center mb-12">Preguntas Frecuentes — Limpieza Profunda en {name}</h2>
              <div className="space-y-4">
                {[
                  { q: `¿Con qué frecuencia debo hacer una limpieza profunda en ${name}?`, a: "Recomendamos una limpieza profunda al menos 1–2 veces al año, o cada vez que notes acumulación de suciedad que la limpieza regular no elimina. También es ideal antes de mudarse, al inicio de la primavera o antes de eventos importantes." },
                  { q: "¿Cuánto tiempo tarda la limpieza profunda?", a: "Entre 4 y 8 horas dependiendo del tamaño y la condición del hogar. Nuestro equipo trabaja con sistemas probados para maximizar la eficiencia sin sacrificar calidad." },
                  { q: "¿Necesito estar presente durante la limpieza?", a: "No. La mayoría de nuestros clientes nos dejan instrucciones de acceso. Llegamos, limpiamos y aseguramos tu hogar cuando terminamos." },
                  { q: "¿Usan productos ecológicos?", a: "Sí. Usamos productos biodegradables y no tóxicos, efectivos contra gérmenes pero seguros para niños, mascotas y el medio ambiente." },
                  { q: `¿Hay diferencia entre limpieza profunda y limpieza de mudanza en ${name}?`, a: "Sí. La limpieza profunda está diseñada para hogares habitados que necesitan un reset. La limpieza de mudanza es específicamente para propiedades vacías al final o inicio de un arrendamiento. Ambas cubren más puntos que la limpieza estándar." },
                  { q: "¿Qué pasa si algo no queda limpio a mi satisfacción?", a: "Nuestra Garantía del 200% lo cubre: si algo no está perfecto, volvemos a limpiar sin costo adicional. Si sigues insatisfecho, te devolvemos el dinero completo." },
                ].map((faq, i) => (
                  <details key={i} className="group border border-border rounded-2xl overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold hover:bg-muted/30 transition-colors">
                      <span>{faq.q}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-open:rotate-90 transition-transform shrink-0 ml-4" />
                    </summary>
                    <div className="p-5 pt-0 text-muted-foreground">{faq.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* ── NEARBY CITIES ────────────────────────────────────────────── */}
          <section className="py-14 bg-background">
            <div className="container">
              <h2 className="text-2xl font-bold text-center mb-8">Limpieza Profunda en Ciudades Cercanas</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {nearbyCities.map((c) => {
                  const s = c.toLowerCase().replace(/\s+/g, "-").replace(/[áéíóú]/g, (ch) => ({ á: "a", é: "e", í: "i", ó: "o", ú: "u" }[ch] || ch));
                  return (
                    <Link key={c} to={`/es/limpieza-profunda/${s}`} className="px-4 py-2 rounded-full border border-primary/30 text-primary text-sm font-medium hover:bg-primary/10 hover:border-primary transition-colors">
                      {c}
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── FINAL CTA ────────────────────────────────────────────────── */}
          <section ref={ctaRef} className={cn("py-24 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-center anim-scale-up", ctaVisible && "is-visible")}>
            <div className="container max-w-3xl">
              <h2 className="text-3xl md:text-5xl font-extrabold mb-4">
                ¿Listo para un hogar transformado en {name}?
              </h2>
              <p className="text-xl opacity-90 mb-10">
                Reserva tu limpieza profunda hoy. Precio exacto en segundos, Garantía del 200%, productos ecológicos.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="h-16 px-12 text-xl font-bold bg-white text-primary hover:bg-white/90 hover:text-primary btn-pulse">
                  <a href={BOOKING_URL}><Zap className="h-5 w-5 mr-2" />Reservar Ahora</a>
                </Button>
                <Button asChild size="lg" className="h-16 px-12 text-xl font-bold bg-white/10 border-2 border-white/40 text-white hover:bg-white/20 hover:text-white">
                  <a href={BOOKING_URL}><Sparkles className="h-5 w-5 mr-2" />Obtener Precio Exacto</a>
                </Button>
              </div>
              <p className="mt-6 text-sm opacity-75">Sin cargos ocultos · Sin tarifas por hora · 100% Garantizado</p>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SpanishDeepCleanPage;
